// src/components/Apm/Search/SearchItem.js

import { useTranslation } from 'react-i18next';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Formik, Form, FastField } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';

import { Grid, Typography } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

import { setSearchInv } from '../../../redux/slices/searchInvSlice';
import { setSearchQuote } from '../../../redux/slices/searchQuoteSlice';
import { appURL } from '../../Data/baseURL';
import Autocomplete from '../../Controls/FormsUI/AutocompleteWrapper';
import AutocompleteItemForItem from '../../Controls/FormsUI/AutocompleteItemForItem';
import ButtonBasic from '../../Controls/Buttons/ButtonBasic';
import DesktopDatePickerBasic from '../../Controls/Inputs/DesktopDatePickerBasic';
import { initialValuesSearchItem } from '../Tables/InitialValues/initialValuesSearchItem';
import SelectBasic from '../../Controls/FormsUI/SelectBasic';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';

const validationSchema = Yup.object({
  load_detail: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  load_address: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  unload_address: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  comment_invs: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  invoice_from: Yup.number()
    .lessThan(99999999, () => i18n.t('yup.max_8_digits'))
    .integer(() => i18n.t('yup.invoice_number_should_be_integer'))
    .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
    .nullable(),
  invoice_to: Yup.number()
    .lessThan(99999999, () => i18n.t('yup.max_8_digits'))
    .integer(() => i18n.t('yup.invoice_number_should_be_integer'))
    .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
    .nullable(),
});

const normalizeSearchValues = (values) => ({
  ...values,

  load_detail: values.load_detail?.trim() || '',

  load_address: values.load_address?.trim() || '',

  unload_address: values.unload_address?.trim() || '',

  comment_invs: values.comment_invs?.trim() || '',
});

const SearchItem = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const theme = useTheme();

  const xsMedia = useMediaQuery(theme.breakpoints.only('xs'));

  const { searchInv } = useSelector((state) => state.searchInvSlice);

  const { searchQuote } = useSelector((state) => state.searchQuoteSlice);

  const isQuotesPage = pathname === `${appURL}/quotes`;

  const currentSearch = isQuotesPage ? searchQuote : searchInv;

  const handleFilter = (values) => {
    const normalizedValues = normalizeSearchValues(values);

    if (isQuotesPage) {
      dispatch(setSearchQuote(normalizedValues));

      return;
    }

    dispatch(setSearchInv(normalizedValues));
  };

  const handleClear = (resetForm) => {
    resetForm({
      values: initialValuesSearchItem,
    });

    if (isQuotesPage) {
      dispatch(setSearchQuote(initialValuesSearchItem));

      return;
    }

    dispatch(setSearchInv(initialValuesSearchItem));
  };

  return (
    <React.Fragment>
      <Formik
        enableReinitialize
        initialValues={currentSearch || initialValuesSearchItem}
        validationSchema={validationSchema}
        onSubmit={handleFilter}
      >
        {({ values, resetForm }) => {
          return (
            <>
              <Form autoComplete="off">
                <Grid container item spacing={1}>
                  {/* Bill to, load details, load address */}
                  <Grid container item xs={12} sm={7} spacing={1}>
                    <Grid item xs={12}>
                      <Autocomplete
                        name="bill_to"
                        label={t(pathname === `${appURL}/quotes` ? 'quote.quote_to' : 'inv.invoice_to')}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FastField as={TextFieldNormal} name="load_detail" label={t('inv.load_details')} />
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item xs={6}>
                        <FastField
                          as={TextFieldNormal}
                          name="load_address"
                          label={t(pathname === `${appURL}/quotes` ? 'quote.loading_port' : 'inv.loading_address')}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <FastField
                          as={TextFieldNormal}
                          name="unload_address"
                          label={t(pathname === `${appURL}/quotes` ? 'quote.unloading_port' : 'inv.unloading_address')}
                          multiline
                        />
                      </Grid>

                      {pathname === `${appURL}/quotes` ? (
                        <Grid container item xs={12} spacing={1}>
                          <Grid item xs={6}>
                            <Autocomplete name="loading_country" label={t('quote.loading_country')} />
                          </Grid>

                          <Grid item xs={6}>
                            <Autocomplete name="unloading_country" label={t('quote.unloading_country')} />
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container item xs={12} spacing={1}>
                          <Grid item xs={12}>
                            <FastField
                              as={TextFieldNormal}
                              name="comment_invs"
                              label={t(pathname === `${appURL}/quotes` ? 'quote.note_to_quote' : 'inv.note_to_invoice')}
                              multiline
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Time period */}
                  <Grid container item xs={12} sm={5} spacing={1}>
                    <Grid container item xs={12} spacing={1}>
                      <Grid item xs={6}>
                        <DesktopDatePickerBasic name="date_min" type="date" label={t('inv.from')} />
                      </Grid>
                      <Grid item xs={6}>
                        <DesktopDatePickerBasic name="date_max" type="date" label={t('inv.until')} />
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item xs={6}>
                        <FastField
                          as={TextFieldNormal}
                          type="number"
                          name="invoice_from"
                          label={t(pathname === `${appURL}/quotes` ? 'quote.quote_hash_from' : 'inv.invoice_hash_from')}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FastField
                          as={TextFieldNormal}
                          type="number"
                          name="invoice_to"
                          label={t(pathname === `${appURL}/quotes` ? 'quote.quote_hash_to' : 'inv.invoice_hash_to')}
                        />
                      </Grid>
                    </Grid>

                    {/* Invoice item / Currency */}
                    <Grid container item xs={12} spacing={1}>
                      <Grid item xs={6}>
                        <AutocompleteItemForItem name="iteminv_invs" label={t('inv.invoice_item')} />
                      </Grid>
                      <Grid item xs={6}>
                        <SelectBasic name="currency" label={t('inv.currency')} />
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item xs={12}>
                        <SelectBasic
                          name="status"
                          label={t('quote.status')}
                          quoteorinv={pathname === `${appURL}/quotes` ? '0' : '1'}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Note to quote / inv, type of load */}
                  {pathname === `${appURL}/quotes` && (
                    <Grid container item xs={12} spacing={1}>
                      <Grid item sm={7} xs={12}>
                        <FastField
                          as={TextFieldNormal}
                          name="comment_invs"
                          label={t(pathname === `${appURL}/quotes` ? 'quote.note_to_quote' : 'inv.note_to_invoice')}
                          multiline
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/* Apply filter & Search button */}
                  <Grid container item xs={12} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <ButtonBasic
                        type="submit"
                        color="primary"
                        variant="outlined"
                        sx={{
                          minWidth: 130,
                          mr: 1,
                        }}
                        fullWidth={xsMedia}
                        startIcon={<FilterAltIcon />}
                      >
                        <Typography
                          variant="button"
                          sx={{
                            pl: 1,
                            pr: 1,
                          }}
                        >
                          {t('button.apply_filter')}
                        </Typography>
                      </ButtonBasic>

                      <ButtonBasic
                        type="button"
                        color="secondary"
                        variant="outlined"
                        sx={{
                          minWidth: 130,
                        }}
                        fullWidth={xsMedia}
                        startIcon={<FilterAltOffIcon />}
                        onClick={() => handleClear(resetForm)}
                      >
                        <Typography
                          variant="button"
                          sx={{
                            pl: 1,
                            pr: 1,
                          }}
                        >
                          {t('button.clear_filter')}
                        </Typography>
                      </ButtonBasic>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            </>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

export default SearchItem;
